// @ts-nocheck
/**
 * User Profile Page
 */

import { useState, useEffect } from "react";
import { useSession } from "@/state/session";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Save, User, Mail, Shield, CreditCard } from "lucide-react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { z } from "zod";

const profileSchema = z.object({
  name: z.string().min(2, "Nome deve ter no mínimo 2 caracteres"),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(6, "Senha atual é obrigatória"),
  newPassword: z.string().min(6, "Nova senha deve ter no mínimo 6 caracteres"),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "As senhas não coincidem",
  path: ["confirmPassword"],
});

export default function Profile() {
  const { user, setSupabaseSession } = useSession();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [name, setName] = useState(user?.name || "");
  const [isUpdating, setIsUpdating] = useState(false);
  
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const [planInfo, setPlanInfo] = useState<any>(null);
  const [isLoadingPlan, setIsLoadingPlan] = useState(true);

  const roleLabels: Record<string, string> = {
    admin: "Administrador",
    gestor: "Gestor",
    funcionario: "Funcionário",
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdating(true);

    try {
      profileSchema.parse({ name });

      const { error } = await supabase
        .from("profiles")
        .update({ name, updated_at: new Date().toISOString() })
        .eq("id", user!.id);

      if (error) throw error;

      const { data: { session } } = await supabase.auth.getSession();
      await setSupabaseSession(session);

      toast({
        title: "Perfil atualizado!",
        description: "Suas informações foram salvas com sucesso.",
      });
    } catch (error: any) {
      toast({
        title: "Erro ao atualizar perfil",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsChangingPassword(true);

    try {
      passwordSchema.parse({ currentPassword, newPassword, confirmPassword });

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user!.email,
        password: currentPassword,
      });

      if (signInError) throw new Error("Senha atual incorreta");

      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      toast({
        title: "Senha alterada!",
        description: "Sua senha foi atualizada com sucesso.",
      });

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      toast({
        title: "Erro ao alterar senha",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  useEffect(() => {
    loadPlanInfo();
  }, [user]);

  const loadPlanInfo = async () => {
    if (!user?.tenant_id) return;

    try {
      const { data: tenant } = await supabase
        .from("tenant")
        .select("subscription_status, current_period_end, plan_id")
        .eq("id", user.tenant_id)
        .single();

      setPlanInfo(tenant);
    } catch (error) {
      console.error("Erro ao carregar plano:", error);
    } finally {
      setIsLoadingPlan(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "--/--/----";
    return new Date(dateString).toLocaleDateString("pt-BR");
  };

  const getPlanLabel = (planId: string | null) => {
    if (!planId) return "Sem plano";
    if (planId.includes("price_1SNhVGRiELkPLD5mypxxv8e6")) return "Basic Mensal";
    if (planId.includes("price_1SNhV2RiELkPLD5mbQVCWgIt")) return "Basic Anual";
    return "Basic";
  };

  const getStatusLabel = (status: string | null) => {
    const labels: Record<string, string> = {
      trialing: "Em Trial",
      active: "Ativo",
      past_due: "Pagamento Atrasado",
      canceled: "Cancelado",
      unpaid: "Não Pago",
      incomplete: "Incompleto",
    };
    return labels[status || ""] || "Sem assinatura";
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Meu Perfil</h1>
          <p className="text-muted-foreground mt-1">
            Gerencie suas informações pessoais e segurança
          </p>
        </div>

        <Tabs defaultValue="personal" className="space-y-6">
          <TabsList>
            <TabsTrigger value="personal">
              <User className="w-4 h-4 mr-2" />
              Informações Pessoais
            </TabsTrigger>
            <TabsTrigger value="plan">
              <CreditCard className="w-4 h-4 mr-2" />
              Meu Plano
            </TabsTrigger>
            <TabsTrigger value="security">
              <Shield className="w-4 h-4 mr-2" />
              Segurança
            </TabsTrigger>
          </TabsList>

          <TabsContent value="personal">
            <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Informações Pessoais
            </CardTitle>
            <CardDescription>
              Atualize suas informações de perfil
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateProfile} className="space-y-6">
              <div className="flex items-center gap-4">
                <Avatar className="w-20 h-20">
                  <AvatarImage src={user?.avatar_url} />
                  <AvatarFallback className="text-lg">
                    {user?.name ? getInitials(user.name) : "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">
                    Avatar será implementado em breve
                  </p>
                </div>
              </div>

              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Seu nome completo"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <div className="flex gap-2">
                    <Input
                      id="email"
                      value={user?.email || ""}
                      disabled
                      className="bg-muted"
                    />
                    <Mail className="w-5 h-5 text-muted-foreground my-auto" />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    O e-mail não pode ser alterado
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role">Função</Label>
                  <div className="flex gap-2">
                    <Input
                      id="role"
                      value={user?.role ? roleLabels[user.role] : "Carregando..."}
                      disabled
                      className="bg-muted"
                    />
                    <Shield className="w-5 h-5 text-muted-foreground my-auto" />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Apenas administradores podem alterar funções
                  </p>
                </div>
              </div>

              <Button type="submit" disabled={isUpdating}>
                {isUpdating ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvar Alterações
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="plan">
            <Card>
              <CardHeader>
                <CardTitle>Plano Atual</CardTitle>
                <CardDescription>
                  Visualize e gerencie sua assinatura
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert>
                  <AlertDescription>
                    Esta seção é apenas para visualização. Para alterar seu plano ou gerenciar pagamentos, acesse a página de cobrança.
                  </AlertDescription>
                </Alert>

                {isLoadingPlan ? (
                  <LoadingSpinner />
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">Status</p>
                      <p className="text-lg font-semibold">{getStatusLabel(planInfo?.subscription_status)}</p>
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">Plano</p>
                      <p className="text-lg font-semibold">{getPlanLabel(planInfo?.plan_id)}</p>
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">Próxima cobrança</p>
                      <p className="text-lg font-semibold">{formatDate(planInfo?.current_period_end)}</p>
                    </div>

                    <Button onClick={() => navigate("/billing")} className="w-full">
                      Gerenciar Plano
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="security">
            <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Segurança
            </CardTitle>
            <CardDescription>
              Altere sua senha para manter sua conta segura
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current-password">Senha Atual</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">Nova Senha</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  minLength={6}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-new-password">Confirmar Nova Senha</Label>
                <Input
                  id="confirm-new-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  minLength={6}
                />
              </div>

              <Button type="submit" disabled={isChangingPassword}>
                {isChangingPassword ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Alterando...
                  </>
                ) : (
                  "Alterar Senha"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
